import { queryOptions, useQueryClient } from '@tanstack/react-query'
import { api } from '../../lib/api'
import { useApiMutation } from '../../data/queries'
import type { IndexingStatus } from '../../components/IndexingIndicator'
import type { DocumentCategory } from './catalog'

export type FileKind = 'document' | 'image' | 'audio' | 'video'
export interface FileVersion {
  indexing?: IndexingStatus
  id: string
  number: number
  filename: string
  contentType: string
  kind: FileKind
  size: number
  notes: string
  createdAt: string
  uploadedBy: string | null
  url: string
}
export interface LibraryItem {
  id: string
  title: string
  description: string
  category: DocumentCategory
  visible: boolean
  searchableByRais: boolean
  citedByRais: boolean
  createdAt: string
  updatedAt: string
  versionCount: number
  latestVersion: FileVersion | null
}
export interface LibraryDetail extends LibraryItem { versions: FileVersion[] }

const pollIndexing = (query: { state: { data?: LibraryItem[] | LibraryDetail } }) => {
  const data = query.state.data
  const versions = Array.isArray(data) ? data.map((item) => item.latestVersion) : data?.versions ?? []
  return versions.some((version) => version && ['queued', 'processing'].includes(version.indexing?.status ?? '')) ? 3000 : false
}
export const documentQueries = {
  list: queryOptions({ queryKey: ['documents', 'list'], refetchInterval: pollIndexing, queryFn: () => api.get<LibraryItem[]>('/documents') }),
  detail: (id: string) => queryOptions({ queryKey: ['documents', 'detail', id], refetchInterval: pollIndexing, queryFn: () => api.get<LibraryDetail>(`/documents/${id}`) }),
  manage: queryOptions({ queryKey: ['manage', 'documents'], refetchInterval: pollIndexing, queryFn: () => api.get<LibraryItem[]>('/manage/documents') }),
  manageDetail: (id: string) => queryOptions({ queryKey: ['manage', 'documents', id], refetchInterval: pollIndexing, queryFn: () => api.get<LibraryDetail>(`/manage/documents/${id}`) }),
}

export type DocumentMetadata = { title: string; description: string; visible: boolean; searchableByRais: boolean; citedByRais: boolean; category: DocumentCategory }

export function useSaveDocument(id?: string, versionOnly = false) {
  const client = useQueryClient()
  return useApiMutation((data: FormData | DocumentMetadata) => {
    const path = `/manage/documents${id ? `/${id}${versionOnly ? '/versions' : ''}` : ''}`
    return id && !versionOnly ? api.patch<LibraryDetail>(path, data) : api.post<LibraryDetail>(path, data)
  }, {
    onData: (item) => {
      client.setQueryData(documentQueries.detail(item.id).queryKey, item)
      client.setQueryData(documentQueries.manageDetail(item.id).queryKey, item)
    },
    invalidate: [['documents'], ['manage', 'documents']],
  })
}

export const fileAccept = '.pdf,.docx,.xlsx,.pptx,.txt,.csv,.jpg,.jpeg,.png,.webp,.gif,.mp3,.wav,.m4a,.ogg,.mp4,.webm,.mov'
export const kindLabels: Record<FileKind, string> = { document: 'Document', image: 'Image', audio: 'Audio', video: 'Video' }
export const fileSize = (bytes: number) => bytes < 1024 ? `${bytes} B` : bytes < 1024 ** 2 ? `${(bytes / 1024).toFixed(1)} KB` : `${(bytes / 1024 ** 2).toFixed(1)} MB`
export const versionDate = (date: string) => new Date(date).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
